import dayjs from "dayjs";
import { formatDuration, formatHoursCompact } from "./utilisation";

// Turns the time logs a user just saved, plus the API's getTimeLogSavedSummary answer for each
// project, into what the "time log saved" popup shows. Everything here is pure.

const TYPE_LABELS = { REGULAR: "Regular", OT: "Overtime" };
const BILLING_LABELS = { BILLABLE: "Billable", "NON-BILLABLE": "Non-billable" };

// "02:30" or "2:30" -> 150. Anything else -> null.
const minutesFromHHMM = (text) => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(text ?? "").trim());
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
};

// How long a log is, from the payload sent to the API: either a typed duration
// (`hours_in_text`) or a start and end time of the same day. null when it cannot be told.
export const minutesFromTimeLogPayload = (payload) => {
  if (!payload) return null;
  if (payload.hours_in_text) return minutesFromHHMM(payload.hours_in_text);

  const start = minutesFromHHMM(payload.start_time);
  const end = minutesFromHHMM(payload.end_time);
  return start !== null && end !== null && end > start ? end - start : null;
};

// One saved log per payload (a single payload or a list). Payloads for no project are skipped.
export const savesFromPayloads = (payloads) =>
  (Array.isArray(payloads) ? payloads : [payloads])
    .filter((payload) => payload && payload.project_id)
    .map((payload) => ({
      projectId: Number(payload.project_id),
      date: payload.log_date || null,
      minutes: minutesFromTimeLogPayload(payload),
      timesheetType: payload.timesheet_type || "REGULAR",
      billingType: payload.billing_type || null,
    }));

// One entry per project, in the order the projects first appear: how many logs and how long.
export const groupSavesByProject = (saves) => {
  const groups = new Map();
  saves.forEach((save) => {
    const group = groups.get(save.projectId) || { projectId: save.projectId, logCount: 0, minutes: 0, unknown: 0 };
    group.logCount += 1;
    if (save.minutes === null) group.unknown += 1;
    else group.minutes += save.minutes;
    groups.set(save.projectId, group);
  });
  return [...groups.values()];
};

// "50h 00m" (what the API sends) -> 3000.
export const parseHoursLabel = (label) => {
  const match = /^(\d+)h\s*(\d{1,2})m$/.exec(String(label ?? "").trim());
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
};

const finite = (value) => (typeof value === "number" && Number.isFinite(value) ? value : null);

// Duration shown for a group of logs; blank when some of their lengths are not known.
const groupDurationLabel = (group) => (group.unknown ? "" : formatDuration(group.minutes));

/**
 * One project's data from `getTimeLogSavedSummary`, as the popup wants it.
 *
 * Fixed Cost projects get the full set of percentages (taken from the *_raw values, never from
 * the rounded text). Anything else - and a Fixed Cost answer that is missing a figure - becomes
 * hours only, so the popup never shows a blank or NaN percentage.
 *
 * @param {object} data   - the `data` of the API response
 * @param {{ logCount: number, minutes: number, unknown?: number }} group - the logs just saved for it
 */
export const mapTimeLogSavedSummary = (data, group = { logCount: 1, minutes: 0 }) => {
  // The hours-only answer has `hours_by_you` at the top level. A Fixed Cost project without a
  // budget is answered with is_fixed_cost:false but the full card, hours inside your_contribution.
  const hoursText = data.hours_by_you ?? data.your_contribution?.hours_by_you;
  const hoursMinutes = parseHoursLabel(hoursText);

  const project = {
    projectId: data.project_id,
    projectName: data.project_name,
    isFixedCost: false,
    logCount: group.logCount,
    durationLabel: groupDurationLabel(group),
    hoursLabel: hoursMinutes !== null ? formatHoursCompact(hoursMinutes) : hoursText ?? "",
  };
  if (!data.is_fixed_cost) return project;

  const individualPct = finite(data.your_contribution?.your_utilisation_raw);
  const projectPct = finite(data.project_health?.budget_used_raw);
  const targetPercent = finite(data.target?.target_percent);
  const targetUsedPct = finite(data.target?.target_used_raw);
  if ([individualPct, projectPct, targetPercent, targetUsedPct].some((value) => value === null)) return project;

  return { ...project, isFixedCost: true, individualPct, projectPct, targetPercent, targetUsedPct };
};

/**
 * The popup's header line: "22/09/2026 · 2h 30m · Regular · Billable" for one log, or
 * "3 time logs saved ... 7h 30m total · 2 projects" for several. `saves` are the logs of the
 * projects that are actually shown.
 */
export const buildSavedHeader = (saves) => {
  const known = saves.filter((save) => save.minutes !== null);
  const totalMinutes = known.reduce((sum, save) => sum + save.minutes, 0);
  const allKnown = known.length === saves.length;
  const single = saves.length === 1 ? saves[0] : null;

  return {
    date: saves[0]?.date ? dayjs(saves[0].date).format("DD/MM/YYYY") : "",
    logCount: saves.length,
    durationLabel: single && single.minutes !== null ? formatDuration(single.minutes) : "",
    totalDurationLabel: allKnown ? formatDuration(totalMinutes) : "",
    typeLabel: single ? TYPE_LABELS[single.timesheetType] || "" : "",
    billingLabel: single ? BILLING_LABELS[single.billingType] || "" : "",
  };
};
